import {
  AGE_BAND_OPTIONS,
  ANAESTHETIC_PRIORITY_OPTIONS,
  ANAESTHETIC_SETTING_OPTIONS,
  ASA_GRADES,
  HILLOS,
  LEARNER_GRADE_OPTIONS,
  anaestheticCaseInputSchema,
  isHilloNumber,
} from '@iculogbook/shared';
import type { AsaGrade, LearnerGrade } from '@iculogbook/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useAuth } from '../auth/AuthProvider.tsx';
import {
  Button,
  ChipGroup,
  Disclosure,
  Field,
  NO_IDENTIFIABLE_INFORMATION,
  SegmentedControl,
  TextArea,
  TextInput,
  useToast,
} from '../components/index.ts';
import { anaestheticRepository } from '../db/repositories/anaesthetic.ts';
import { CapabilityLevelField } from '../features/log/CapabilityLevelField.tsx';
import { OccurredAtField } from '../features/log/OccurredAtField.tsx';
import type { FormErrors } from '../features/log/ProcedureForm.tsx';
import {
  anaestheticValuesFrom,
  buildAnaestheticInput,
} from '../features/log/anaestheticForm.ts';
import type { AnaestheticFormValues } from '../features/log/anaestheticForm.ts';

const TITLE_MAX_LENGTH = 140;

const SETTING_SEGMENTS = ANAESTHETIC_SETTING_OPTIONS.map((option) => ({
  value: option.key,
  label: option.label,
}));

/**
 * Editing an anaesthetic case.
 *
 * There is no matching "new" route, on purpose: these arrive by import from the
 * Anaesthetics Lifelong Learning Platform, which is the system of record for
 * them. This screen exists so a trainee can correct or delete something an
 * import got wrong — which they must be able to do, or the import is a one-way
 * door.
 */
export function EditAnaestheticRoute() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { anaestheticId } = useParams();

  const userId = user?.id ?? null;

  const stored = useLiveQuery(
    async () =>
      userId === null || anaestheticId === undefined
        ? null
        : ((await anaestheticRepository.get(userId, anaestheticId)) ?? null),
    [userId, anaestheticId],
  );

  const [values, setValues] = useState<AnaestheticFormValues | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  /* Seed once — a live query must never overwrite something half-typed. */
  const [seeded, setSeeded] = useState(false);
  if (!seeded && values === null && stored !== undefined && stored !== null) {
    setSeeded(true);
    setValues(anaestheticValuesFrom(stored));
  }

  function set<K extends keyof AnaestheticFormValues>(
    key: K,
    value: AnaestheticFormValues[K],
  ): void {
    setValues((current) => (current === null ? current : { ...current, [key]: value }));
  }

  async function save(): Promise<void> {
    if (values === null || userId === null || anaestheticId === undefined) return;

    const input = buildAnaestheticInput(values);
    if (input === null) {
      setErrors({
        ...(values.title.trim() === '' ? { title: 'Give this a short title.' } : {}),
        ...(values.ageBand === null ? { ageBand: 'Choose an age band.' } : {}),
        ...(values.capabilityLevel === null
          ? { capabilityLevel: 'Choose a capability level.' }
          : {}),
      });
      return;
    }

    const parsed = anaestheticCaseInputSchema.safeParse(input);
    if (!parsed.success) {
      const collected: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === 'string' && collected[field] === undefined) {
          collected[field] = issue.message;
        }
      }
      setErrors(collected);
      return;
    }

    setSaving(true);
    try {
      await anaestheticRepository.update(userId, anaestheticId, parsed.data);
      showToast({ message: 'Anaesthetic case updated.', tone: 'success' });
      void navigate('/entries');
    } finally {
      setSaving(false);
    }
  }

  if (stored === null) {
    return <p className="text-sm text-muted">That anaesthetic case no longer exists.</p>;
  }

  if (values === null) return null;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
      className="flex flex-col gap-4"
      noValidate
    >
      <p className="rounded-md border border-line bg-surface-sunken px-4 py-3 text-xs text-muted">
        Anaesthetic cases are imported from the Lifelong Learning Platform and counted separately
        from your ICM work on the ARCP summary. You can correct or delete one here.
      </p>

      <SegmentedControl
        label="Setting"
        segments={SETTING_SEGMENTS}
        value={values.setting}
        onChange={(next) => set('setting', next)}
        className="h-12"
      />

      <Field
        label="Operation or review"
        help={NO_IDENTIFIABLE_INFORMATION}
        error={errors['title']}
        required
      >
        <TextInput
          value={values.title}
          onChange={(event) => set('title', event.target.value)}
          maxLength={TITLE_MAX_LENGTH}
        />
      </Field>

      <CapabilityLevelField
        value={values.capabilityLevel}
        onChange={(level) => set('capabilityLevel', level)}
        error={errors['capabilityLevel']}
      />

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text">Age band</span>
        <ChipGroup
          label="Age band"
          options={AGE_BAND_OPTIONS.map((option) => ({ value: option.key, label: option.label }))}
          value={values.ageBand}
          onChange={(next) => set('ageBand', next)}
        />
        {errors['ageBand'] === undefined ? null : (
          <p role="alert" className="text-xs text-danger">
            {errors['ageBand']}
          </p>
        )}
      </div>

      <OccurredAtField
        value={values.occurredAt}
        onChange={(next) => set('occurredAt', next)}
        error={errors['occurredAt']}
      />

      <Disclosure
        title="Priority and ASA"
        hint="Optional. NCEPOD classification and physical status."
        badge={
          [values.priority, values.asa].filter((part) => part !== null).length === 0
            ? null
            : 'Set'
        }
      >
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">Priority</span>
          <ChipGroup
            label="Priority"
            options={ANAESTHETIC_PRIORITY_OPTIONS.map((option) => ({
              value: option.key,
              label: option.label,
            }))}
            value={values.priority}
            onChange={(next) => set('priority', next)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">ASA</span>
          <ChipGroup
            label="ASA physical status"
            options={ASA_GRADES.map((grade) => ({
              value: String(grade),
              label: String(grade),
            }))}
            value={values.asa === null ? null : String(values.asa)}
            onChange={(next) => set('asa', next === null ? null : (Number(next) as AsaGrade))}
          />
        </div>
      </Disclosure>

      <Disclosure
        title="Teaching or supervising"
        hint="Optional. Who you taught or supervised during this case."
        badge={
          values.teachingGrades.length === 0
            ? null
            : `${String(values.teachingGrades.length)} chosen`
        }
      >
        <ChipGroup
          label="Who you taught or supervised"
          multiple
          options={LEARNER_GRADE_OPTIONS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={values.teachingGrades}
          onChange={(next) => set('teachingGrades', next as LearnerGrade[])}
        />
      </Disclosure>

      <Disclosure
        title="Note"
        hint="Anything worth remembering about this case"
        badge={values.note.trim() === '' ? null : 'Written'}
      >
        <Field label="Note" help={NO_IDENTIFIABLE_INFORMATION} error={errors['note']}>
          <TextArea
            value={values.note}
            onChange={(event) => set('note', event.target.value)}
            maxLength={500}
            rows={3}
          />
        </Field>
      </Disclosure>

      <Disclosure
        title="Curriculum"
        hint="Optional HiLLO tagging. HiLLO 10 is Anaesthesia."
        badge={values.hillos.length === 0 ? null : `${String(values.hillos.length)} tagged`}
      >
        <ChipGroup
          label="High Level Learning Outcomes"
          multiple
          options={HILLOS.map((hillo) => ({
            value: String(hillo.number),
            label: `${String(hillo.number)}. ${hillo.shortLabel}`,
          }))}
          value={values.hillos.map(String)}
          onChange={(next) => set('hillos', next.map(Number).filter(isHilloNumber))}
        />
      </Disclosure>

      <Button type="submit" variant="primary" block disabled={saving} className="h-14">
        Save changes
      </Button>
    </form>
  );
}
